<template>
    <teleport to="body">
        <div v-if="hotspots.length" ref="layerRef" class="page-hotspot-layer" :style="{ height: layerHeight + 'px' }">
            <div v-for="item in hotspots" :key="item.id" class="hotspot-rect" :style="boxStyle(item)"
                @click="openHotspot(item)"></div>
        </div>
    </teleport>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { HotspotList, normalizeHotspot, unwrapHotspotList } from '@/api/hotspot'
import { hotspotPages, isProductDetailRoute } from '@/hotspots/placements'
import { pageRectFromHotspot } from '@/hotspots/anchor'

const route = useRoute()
const router = useRouter()
const hotspots = ref([])
const layerRef = ref(null)
const layerHeight = ref(0)
const frames = ref({})

let observer = null
let syncing = false
let requestId = 0

const inFrame = () => window.self !== window.top

const boxStyle = (item) => frames.value[item.id] || { display: 'none' }

const placeHotspots = () => {
    const layer = layerRef.value
    if (!layer) return
    const next = {}
    hotspots.value.forEach((item) => {
        const page = pageRectFromHotspot(document, layer, item.hotspot)
        if (!page) {
            next[item.id] = { display: 'none' }
            return
        }
        next[item.id] = {
            left: `${page.x}%`,
            top: `${page.y}%`,
            width: `${page.width}%`,
            height: `${page.height}%`,
        }
    })
    frames.value = next
}

const syncHeight = () => {
    const layer = layerRef.value
    if (!layer || syncing) return
    syncing = true
    layer.style.height = '0px'
    const height = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight)
    syncing = false
    if (height > 0 && height !== layerHeight.value) {
        layerHeight.value = height
    } else {
        layer.style.height = `${layerHeight.value}px`
    }
}

const updatePointer = () => {
    const layer = layerRef.value
    if (!layer) return
    const header = document.querySelector('.header')
    const headerHeight = header ? header.getBoundingClientRect().height : 0
    layer.querySelectorAll('.hotspot-rect').forEach((el) => {
        const rect = el.getBoundingClientRect()
        const overlapsHeader = headerHeight > 0 && rect.top < headerHeight && rect.bottom > 0
        const belongsToHeader = el.offsetTop < headerHeight
        el.style.pointerEvents = overlapsHeader && !belongsToHeader ? 'none' : 'auto'
    })
}

const syncLayer = () => {
    syncHeight()
    placeHotspots()
    updatePointer()
}

const onImageLoad = (event) => {
    if (event.target?.tagName === 'IMG') syncLayer()
}

const onScroll = () => {
    placeHotspots()
    updatePointer()
}

const unbind = () => {
    observer?.disconnect()
    observer = null
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('resize', syncLayer)
    document.removeEventListener('load', onImageLoad, true)
}

const bindObserver = () => {
    unbind()
    if (!hotspots.value.length) return
    observer = new ResizeObserver(() => syncLayer())
    observer.observe(document.documentElement)
    observer.observe(document.body)
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', syncLayer)
    document.addEventListener('load', onImageLoad, true)
}

const resetLayer = () => {
    hotspots.value = []
    frames.value = {}
    unbind()
}

const hotspotEnabled = (path) => !inFrame() && (
    hotspotPages.some((item) => item.route === path) || isProductDetailRoute(path)
)

const load = async (path = route.path) => {
    const current = ++requestId
    if (!hotspotEnabled(path)) {
        resetLayer()
        return
    }
    try {
        const res = await HotspotList(path)
        if (current !== requestId || route.path !== path) return
        hotspots.value = unwrapHotspotList(res)
            .map(normalizeHotspot)
            .filter((item) => item.hotspot.width > 0 && item.hotspot.height > 0)
    } catch {
        if (current !== requestId || route.path !== path) return
        hotspots.value = []
    }
    if (!hotspots.value.length) {
        unbind()
        return
    }
    document.body.style.position = 'relative'
    await nextTick()
    if (current !== requestId || route.path !== path) return
    syncLayer()
    bindObserver()
}

const openHotspot = (item) => {
    const spot = item.hotspot
    if (spot.link_type === 'external') {
        if (spot.url) window.open(spot.url, '_blank', 'noopener')
        return
    }
    if (!spot.product_id) return
    router.push('/product/detail/' + (spot.product_name || 'product') + '/' + spot.product_id)
}

watch(() => route.path, (path) => {
    resetLayer()
    load(path)
}, { flush: 'post' })
onMounted(() => load(route.path))
onBeforeUnmount(unbind)
</script>

<style scoped>
.page-hotspot-layer {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    z-index: 1000;
    pointer-events: none;
}

.hotspot-rect {
    position: absolute;
    cursor: pointer;
    z-index: 10;
    pointer-events: auto;
    background-color: transparent;
}

.hotspot-rect:hover {
    background-color: rgba(255, 255, 255, 0.2);
}
</style>
